import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Pencil } from "lucide-react";
import { TypeBadge, StatusPill } from "../ClientBadges";
import { initials, avatarColor, displayName, fmtCurrency, fmtDate } from "../clientConstants";
import { OverviewPanel } from "./OverviewPanel";
import { OrdersPanel } from "./OrdersPanel";
import { InvoicesPanel } from "./InvoicesPanel";
import { DeliveriesPanel } from "./DeliveriesPanel";
import { NotesPanel } from "./NotesPanel";

const tabCls = "rounded-none data-[state=active]:bg-[#0C2347] data-[state=active]:text-white font-mono text-[11px] uppercase tracking-[0.1em]";

export function ClientProfileDialog({ client, onClose, onEdit, onOpenOrder, onCreateOrder, refreshKey }) {
  const [profile, setProfile] = useState(null);
  const load = () => api.get(`/admin/clients/${client.id}/profile`).then((r) => setProfile(r.data)).catch(() => toast.error("Could not load client profile"));
  useEffect(() => { load(); }, [client.id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const c = profile || client;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="client-profile-dialog" className="max-w-6xl w-[96vw] h-[90vh] rounded-xl p-0 flex flex-col overflow-hidden">
        <DialogTitle className="sr-only">{displayName(c)} profile</DialogTitle><DialogDescription className="sr-only">Overview, orders, invoices, deliveries, and notes for this client.</DialogDescription>
        <div className="px-6 md:px-8 pt-6 pb-5 border-b border-[#E4E9F0] flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {c.photo_url ? (
              <img data-testid="client-profile-photo" src={c.photo_url} alt={displayName(c)} className="h-14 w-14 shrink-0 rounded-full object-cover border border-[#E4E9F0]" />
            ) : (
              <span data-testid="client-profile-avatar-initials" className="h-14 w-14 shrink-0 rounded-full grid place-items-center text-lg font-semibold text-[#101828]" style={{ backgroundColor: avatarColor(c.email) }}>{initials(displayName(c))}</span>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2"><h2 className="font-serif-display text-2xl sm:text-3xl text-[#101828] truncate">{displayName(c)}</h2><TypeBadge type={c.client_type} /><StatusPill status={c.status} /></div>
              <div className="text-sm text-[#475467] mt-1 truncate flex items-center gap-2">
                {c.logo_url && <img data-testid="client-profile-brokerage-logo" src={c.logo_url} alt={`${c.company || "Brokerage"} logo`} className="h-6 max-w-[80px] object-contain shrink-0" />}
                <span className="truncate">{c.company ? `${c.company} · ` : ""}{c.email}{c.phone ? ` · ${c.phone}` : ""}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button data-testid="client-profile-create-order" variant="outline" disabled={c.status === "archived"} title={c.status === "archived" ? "Restore this client before creating a new order for them" : undefined} onClick={() => onCreateOrder(c)} className="rounded-lg gap-2 disabled:opacity-50 disabled:cursor-not-allowed"><Plus size={14} /> Create order</Button>
            <Button data-testid="client-profile-edit" onClick={() => onEdit(c)} className="rounded-lg gap-2 bg-[#0C2347] hover:bg-[#123262] text-white"><Pencil size={14} /> Edit client</Button>
          </div>
        </div>

        <div className="px-6 md:px-8 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-[#E4E9F0]">
          <MiniStat testId="client-profile-stat-orders" label="Total Orders" value={c.orders_count ?? 0} />
          <MiniStat testId="client-profile-stat-billed" label="Total Billed" value={fmtCurrency(c.total_billed)} />
          <MiniStat testId="client-profile-stat-balance" label="Outstanding Balance" value={fmtCurrency(c.outstanding_balance)} />
          <MiniStat testId="client-profile-stat-last-order" label="Last Order" value={fmtDate(c.last_order_at)} />
        </div>

        <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 md:mx-8 mt-4 bg-white border border-[#E4E9F0] rounded-lg p-1 h-auto flex-wrap self-start">
            <TabsTrigger value="overview" data-testid="client-profile-tab-overview" className={tabCls}>Overview</TabsTrigger>
            <TabsTrigger value="orders" data-testid="client-profile-tab-orders" className={tabCls}>Orders</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="client-profile-tab-invoices" className={tabCls}>Invoices &amp; Payments</TabsTrigger>
            <TabsTrigger value="deliveries" data-testid="client-profile-tab-deliveries" className={tabCls}>Gallery</TabsTrigger>
            <TabsTrigger value="notes" data-testid="client-profile-tab-notes" className={tabCls}>Notes</TabsTrigger>
          </TabsList>
          <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
            <TabsContent value="overview" className="mt-0"><OverviewPanel client={c} /></TabsContent>
            <TabsContent value="orders" className="mt-0"><OrdersPanel clientId={client.id} onOpenOrder={onOpenOrder} /></TabsContent>
            <TabsContent value="invoices" className="mt-0"><InvoicesPanel clientId={client.id} onOpenOrder={onOpenOrder} /></TabsContent>
            <TabsContent value="deliveries" className="mt-0"><DeliveriesPanel clientId={client.id} onOpenOrder={onOpenOrder} /></TabsContent>
            <TabsContent value="notes" className="mt-0"><NotesPanel clientId={client.id} /></TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ testId, label, value }) {
  return <div data-testid={testId}><div className="text-[11px] uppercase tracking-[0.12em] text-[#94A3B8]">{label}</div><div className="text-xl font-semibold text-[#101828] mt-1 truncate">{value}</div></div>;
}
